import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Loader2, Phone, Mail, AlertTriangle, Globe, Info } from 'lucide-react';
import { getFriendlyError } from '../utils/friendlyErrors';
import COUNTRY_CODES, { isIndianCode } from '../constants/countryCodes';

// Google Icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// Google OAuth handler
const handleGoogleSignIn = (role) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const currentOrigin = window.location.origin;
  window.location.href = `${backendUrl}/api/auth/google?role=${role}&redirect_url=${encodeURIComponent(currentOrigin)}`;
};

const RegisterNew = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Mobile OTP state
  const [mobile, setMobile] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  
  // Auto-detect country from IP on mount
  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(data => {
        const iso = data.country_code; // e.g., 'US', 'IN', 'AE'
        if (iso) {
          const match = COUNTRY_CODES.find(c => c.label.startsWith(iso + ' '));
          if (match) setCountryCode(match.code);
        }
      })
      .catch(() => {}); // Fail silently
  }, []);
  const [hasPan, setHasPan] = useState(null); // null = not asked, true/false for non-India users
  const [mobileOtp, setMobileOtp] = useState('');
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  
  // Email OTP state
  const [email, setEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  
  // Email validation error
  const [emailError, setEmailError] = useState('');
  
  // Account exists warning
  const [accountExists, setAccountExists] = useState(null);
  
  // Email validation helper - checks for valid format with proper domain
  const validateEmail = (emailValue) => {
    if (!emailValue) return { valid: false, error: '' };
    
    // Basic format check
    const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicRegex.test(emailValue)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }
    
    // Extract domain
    const domain = emailValue.split('@')[1];
    if (!domain) {
      return { valid: false, error: 'Invalid email format' };
    }
    
    // Check domain has at least 2 characters after the dot (e.g., .com, .in, .io)
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) {
      return { valid: false, error: 'Please enter a complete email address with valid domain' };
    }
    
    // Check for common typos in popular domains
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'mail.com', 'protonmail.com', 'rediffmail.com'];
    const suspiciousPatterns = [
      { pattern: /^gma\.com$/i, suggestion: 'gmail.com' },
      { pattern: /^gmial\.com$/i, suggestion: 'gmail.com' },
      { pattern: /^gmai\.com$/i, suggestion: 'gmail.com' },
      { pattern: /^gamil\.com$/i, suggestion: 'gmail.com' },
      { pattern: /^yaho\.com$/i, suggestion: 'yahoo.com' },
      { pattern: /^yahooo\.com$/i, suggestion: 'yahoo.com' },
      { pattern: /^hotmal\.com$/i, suggestion: 'hotmail.com' },
      { pattern: /^outlok\.com$/i, suggestion: 'outlook.com' },
    ];
    
    for (const { pattern, suggestion } of suspiciousPatterns) {
      if (pattern.test(domain)) {
        return { valid: false, error: `Did you mean ${suggestion}?` };
      }
    }
    
    // Check if domain is too short (likely incomplete)
    if (domain.length < 5) {
      return { valid: false, error: 'Please enter a complete email domain' };
    }
    
    return { valid: true, error: '' };
  };

  // ============= MOBILE OTP FLOW =============
  
  const isIndia = isIndianCode(countryCode);
  const dialCode = countryCode.replace('CA', ''); // Handle Canada's '1CA' → '1'

  const checkMobileExists = async () => {
    // Validate mobile based on country
    if (isIndia) {
      if (!mobile || mobile.length !== 10 || !/^[6-9]\d{9}$/.test(mobile)) {
        toast({ title: 'Error', description: 'Please enter a valid 10-digit Indian mobile number (starting with 6-9)', variant: 'destructive' });
        return false;
      }
    } else {
      if (!mobile || mobile.length < 5 || mobile.length > 15) {
        toast({ title: 'Error', description: 'Please enter a valid mobile number', variant: 'destructive' });
        return false;
      }
    }
    
    setLoading(true);
    try {
      const result = await apiCall('/api/auth/check-phone', {
        method: 'POST',
        body: JSON.stringify({ mobile })
      });
      
      if (result.exists) {
        setAccountExists({
          type: 'mobile',
          message: result.message,
          masked: result.masked_email,
          suggestion: result.suggestion
        });
        return false;
      }
      
      setAccountExists(null);
      return true;
    } catch (error) {
      toast({ title: 'Something went wrong', description: getFriendlyError(error), variant: 'destructive' });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Mobile validation — country-aware
  const isMobileValid = isIndia
    ? (mobile.length === 10 && /^[6-9]\d{9}$/.test(mobile))
    : (mobile.length >= 5 && mobile.length <= 15);

  const sendMobileOtp = async (isResend = false) => {
    // For resend, skip the exists check since we already verified
    if (!isResend) {
      const canProceed = await checkMobileExists();
      if (!canProceed) return;
    } else {
      if (!isMobileValid) {
        toast({ title: 'Error', description: 'Please enter a valid mobile number', variant: 'destructive' });
        return;
      }
    }
    
    setLoading(true);
    try {
      const result = await apiCall('/api/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ mobile, country_code: dialCode })
      });
      
      setMobileOtpSent(true);
      toast({ title: 'OTP Sent', description: isResend ? 'New OTP sent to your mobile' : 'Please check your mobile for the verification code' });
      
      // For development, show OTP in console
      if (result.otp) {
      }
    } catch (error) {
      toast({ title: 'Something went wrong', description: getFriendlyError(error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const verifyMobileOtp = async () => {
    if (!mobileOtp || mobileOtp.length !== 6) {
      toast({ title: 'Error', description: 'Please enter a valid 6-digit OTP', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      await apiCall('/api/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ mobile, otp: mobileOtp })
      });
      
      toast({ title: 'Success', description: 'Mobile verified! Now set up your account.' });
      
      // Store verified mobile in localStorage with 60-minute expiry
      const regState = {
        verified_mobile: mobile,
        country_code: dialCode,
        is_international: !isIndia,
        has_indian_pan: isIndia ? true : hasPan === true,
        verification_method: 'mobile',
        timestamp: Date.now(),
      };
      localStorage.setItem('aniyor_reg_state', JSON.stringify(regState));
      navigate('/setup-account');
      
    } catch (error) {
      toast({ title: 'Something went wrong', description: getFriendlyError(error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ============= EMAIL OTP FLOW =============
  
  const checkEmailExists = async () => {
    // First validate email format
    const validation = validateEmail(email);
    if (!validation.valid) {
      setEmailError(validation.error);
      toast({ title: 'Error', description: validation.error, variant: 'destructive' });
      return false;
    }
    
    setEmailError('');
    setLoading(true);
    try {
      const result = await apiCall('/api/auth/check-email', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      
      if (result.exists) {
        setAccountExists({
          type: 'email',
          message: result.message,
          masked: result.masked_mobile,
          suggestion: result.suggestion
        });
        return false;
      }
      
      setAccountExists(null);
      return true;
    } catch (error) {
      toast({ title: 'Something went wrong', description: getFriendlyError(error), variant: 'destructive' });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendEmailOtp = async (isResend = false) => {
    // For resend, skip the exists check since we already verified
    if (!isResend) {
      const canProceed = await checkEmailExists();
      if (!canProceed) return;
    } else {
      // Still validate email format on resend
      const validation = validateEmail(email);
      if (!validation.valid) {
        toast({ title: 'Error', description: validation.error, variant: 'destructive' });
        return;
      }
    }
    
    setLoading(true);
    try {
      const result = await apiCall('/api/auth/send-email-otp', {
        method: 'POST',
        body: JSON.stringify({ email, purpose: 'verification' })
      });
      
      setEmailOtpSent(true);
      toast({ title: 'OTP Sent', description: 'Please check your email for the verification code' });
      
      // For development, show OTP in console
      if (result.otp) {
      }
    } catch (error) {
      toast({ title: 'Something went wrong', description: getFriendlyError(error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!emailOtp || emailOtp.length !== 6) {
      toast({ title: 'Error', description: 'Please enter a valid 6-digit OTP', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      await apiCall('/api/auth/verify-email-otp', {
        method: 'POST',
        body: JSON.stringify({ email, otp: emailOtp })
      });
      
      toast({ title: 'Success', description: 'Email verified! Now set up your account.' });
      
      // Store verified email in localStorage with 60-minute expiry
      const regState = {
        verified_email: email,
        verification_method: 'email',
        timestamp: Date.now(),
      };
      localStorage.setItem('aniyor_reg_state', JSON.stringify(regState));
      navigate('/setup-account');
      
    } catch (error) {
      toast({ title: 'Something went wrong', description: getFriendlyError(error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ============= ACCOUNT EXISTS ALERT =============
  
  const renderAccountExistsAlert = () => {
    if (!accountExists) return null;
    
    return (
      <Alert className="border-amber-200 bg-amber-50 mb-4">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <p className="font-medium">{accountExists.message}</p>
          <p className="text-sm mt-1">{accountExists.suggestion}</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => navigate('/login')} className="bg-amber-600 hover:bg-amber-700">
              Login Instead
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/forgot-password')}>
              Forgot Password
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Create Account</CardTitle>
          <CardDescription>
            Verify your identity to get started
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Google Sign Up */}
          <Button
            variant="outline"
            className="w-full mb-4"
            onClick={() => handleGoogleSignIn('vendor')}
          >
            <GoogleIcon />
            Continue with Google
          </Button>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or verify with</span>
            </div>
          </div>

          <Tabs defaultValue="mobile" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="mobile" className="flex items-center gap-1">
                <Phone size={14} /> Mobile
              </TabsTrigger>
              <TabsTrigger value="email" className="flex items-center gap-1">
                <Mail size={14} /> Email
              </TabsTrigger>
            </TabsList>

            {/* Mobile OTP Tab */}
            <TabsContent value="mobile" className="space-y-4">
              {renderAccountExistsAlert()}
              
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number</Label>
                <div className="flex gap-2">
                  <div className="flex flex-1">
                    <select
                      className="h-9 rounded-l-md border border-r-0 border-input bg-white px-2 text-sm focus-visible:outline-none w-[110px]"
                      data-testid="register-country-code-select"
                      value={countryCode}
                      onChange={(e) => {
                        setCountryCode(e.target.value);
                        setMobileOtpSent(false);
                        setMobileOtp('');
                        // Reset PAN toggle when switching countries
                        if (isIndianCode(e.target.value)) {
                          setHasPan(null);
                        } else {
                          setHasPan(false); // Default to No for non-India
                        }
                      }}
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} +{c.code.replace('CA', '')}
                        </option>
                      ))}
                    </select>
                    <Input
                      id="mobile"
                      data-testid="register-mobile-input"
                      value={mobile}
                      onChange={(e) => {
                        const maxLen = isIndia ? 10 : 15;
                        setMobile(e.target.value.replace(/\D/g, '').slice(0, maxLen));
                        setAccountExists(null);
                        setMobileOtpSent(false);
                      }}
                      placeholder={isIndia ? '9876543210' : 'Mobile number'}
                      className="rounded-l-none"
                      maxLength={isIndia ? 10 : 15}
                      disabled={mobileOtpSent}
                    />
                  </div>
                  {!mobileOtpSent && (
                    <Button onClick={() => sendMobileOtp(false)} disabled={loading || !isMobileValid} data-testid="register-send-otp-btn">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send OTP'}
                    </Button>
                  )}
                </div>
                {isIndia && (
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Globe size={10} /> India (+91) — full marketplace access after verification
                  </p>
                )}
              </div>

              {/* PAN Toggle — shown when non-India country is selected */}
              {!isIndia && !mobileOtpSent && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="pan-toggle-section">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">Do you have an Indian PAN?</p>
                      <p className="text-xs text-blue-700 mt-1">NRIs and Expats with an Indian PAN get full transactional access.</p>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-6">
                    <Button
                      size="sm"
                      variant={hasPan === true ? 'default' : 'outline'}
                      className={`h-8 ${hasPan === true ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      onClick={() => setHasPan(true)}
                      data-testid="pan-toggle-yes"
                    >
                      Yes, I have PAN
                    </Button>
                    <Button
                      size="sm"
                      variant={hasPan === false ? 'default' : 'outline'}
                      className={`h-8 ${hasPan === false ? 'bg-gray-700 hover:bg-gray-800' : ''}`}
                      onClick={() => setHasPan(false)}
                      data-testid="pan-toggle-no"
                    >
                      No
                    </Button>
                  </div>
                  {hasPan === false && (
                    <p className="text-xs text-blue-700 ml-6 mt-1" data-testid="leads-only-note">
                      Without an Indian PAN, your account will be limited to <strong>Lead Generation</strong> for now.
                    </p>
                  )}
                  {hasPan === true && (
                    <p className="text-xs text-green-700 ml-6 mt-1">
                      Great! You'll get full access after completing tax registration.
                    </p>
                  )}
                </div>
              )}

              {mobileOtpSent && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">
                    OTP sent to <strong>+{dialCode} {mobile}</strong>
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter 6-digit OTP"
                      value={mobileOtp}
                      onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                    />
                    <Button onClick={verifyMobileOtp} disabled={loading || mobileOtp.length !== 6}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <button onClick={() => sendMobileOtp(true)} className="text-blue-600 hover:underline" disabled={loading}>
                      Resend OTP
                    </button>
                    <span className="text-gray-300">|</span>
                    <button 
                      onClick={() => { setMobileOtpSent(false); setMobileOtp(''); }}
                      className="text-gray-500 hover:underline"
                    >
                      Change Number
                    </button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Email OTP Tab */}
            <TabsContent value="email" className="space-y-4">
              {renderAccountExistsAlert()}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="flex gap-2">
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const newEmail = e.target.value;
                      setEmail(newEmail);
                      setAccountExists(null);
                      setEmailOtpSent(false);
                      // Validate on change and show error
                      if (newEmail) {
                        const validation = validateEmail(newEmail);
                        setEmailError(validation.error);
                      } else {
                        setEmailError('');
                      }
                    }}
                    placeholder="you@example.com"
                    className={`flex-1 ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    disabled={emailOtpSent}
                  />
                  {!emailOtpSent && (
                    <Button onClick={() => sendEmailOtp(false)} disabled={loading || !validateEmail(email).valid}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send OTP'}
                    </Button>
                  )}
                </div>
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
              </div>

              {emailOtpSent && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">
                    OTP sent to <strong>{email}</strong>
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter 6-digit OTP"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                    />
                    <Button onClick={verifyEmailOtp} disabled={loading || emailOtp.length !== 6}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <button onClick={() => sendEmailOtp(true)} className="text-blue-600 hover:underline" disabled={loading}>
                      Resend OTP
                    </button>
                    <span className="text-gray-300">|</span>
                    <button 
                      onClick={() => { setEmailOtpSent(false); setEmailOtp(''); setEmailError(''); }}
                      className="text-gray-500 hover:underline"
                    >
                      Change Email
                    </button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:underline font-medium">
              Login here
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterNew;
